/**
 * 오프라인 저장 시스템
 * - IndexedDB 기반 레포트 및 이미지 저장
 * - 이미지와 레포트 데이터 분리 관리
 * - 동기화 메타데이터 관리
 * - 디버깅 패널의 IndexedDB 헬퍼 함수들 기반으로 확장
 */

import { autoCompressIfNeeded } from './imageCompression';

class OfflineStorage {
  constructor() {
    this.dbName = 'FDES_OfflineDB';
    this.dbVersion = 2; // 이미지 스토어 추가로 버전 업
    this.stores = {
      reports: 'reports',
      images: 'images',
      sync_status: 'sync_status'
    };
    this.listeners = new Set(); // 🆕 리스너 시스템 추가
  }

  /**
   * 🆕 리스너 추가
   */
  addListener(callback) {
    this.listeners.add(callback);
  }

  /**
   * 🆕 리스너 제거
   */
  removeListener(callback) {
    this.listeners.delete(callback);
  }

  /**
   * 🆕 모든 리스너들에게 이벤트 알림
   */
  notifyListeners(event, data) {
    this.listeners.forEach(callback => {
      try {
        callback(event, data);
      } catch (error) {
        console.error('[OfflineStorage] 리스너 실행 에러:', error);
      }
    });
  }

  /**
   * IndexedDB 오픈 (기존 디버깅 패널 로직 확장)
   */
  openIndexedDB() {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(this.dbName, this.dbVersion);

      request.onerror = () => {
        // console.error('[OfflineStorage] IndexedDB 열기 실패:', request.error);
        reject(request.error);
      };

      request.onsuccess = () => {
        resolve(request.result);
      };

      request.onupgradeneeded = (event) => {
        const db = event.target.result;

        // 레포트 스토어 (기존 구조 유지)
        if (!db.objectStoreNames.contains(this.stores.reports)) {
          const reportsStore = db.createObjectStore(this.stores.reports, { keyPath: 'id' });
          reportsStore.createIndex('timestamp', 'timestamp', { unique: false });
          reportsStore.createIndex('status', 'status', { unique: false });
        }

        // 이미지 스토어 (신규 추가)
        if (!db.objectStoreNames.contains(this.stores.images)) {
          const imagesStore = db.createObjectStore(this.stores.images, { keyPath: 'id' });
          imagesStore.createIndex('reportId', 'reportId', { unique: false });
          imagesStore.createIndex('fieldPath', 'fieldPath', { unique: false });
        }

        // 동기화 상태 스토어 (신규 추가)
        if (!db.objectStoreNames.contains(this.stores.sync_status)) {
          const syncStore = db.createObjectStore(this.stores.sync_status, { keyPath: 'id' });
          syncStore.createIndex('type', 'type', { unique: false });
        }

        // console.log('[OfflineStorage] IndexedDB 스키마 업그레이드 완료');
      };
    });
  }

  /**
   * 레포트 저장 (이미지 분리 처리)
   */
  async saveReport(report) {
    try {
      // console.log('[OfflineStorage] 레포트 저장 시작:', report.id || '신규');

      // 1. 레포트 ID 생성 (없는 경우)
      if (!report.id) {
        report.id = this.generateId();
      }

      // 2. 이미지 추출 및 별도 저장
      const imageIds = await this.extractAndSaveImages(report);

      // 3. 레포트 데이터에서 이미지 File 객체 제거 후 이미지 ID로 교체
      const sanitizedReport = this.replaceImagesWithIds(report, imageIds);

      // 4. 메타데이터 추가
      sanitizedReport.offlineMetadata = {
        savedAt: new Date().toISOString(),
        hasImages: imageIds.length > 0,
        imageIds: imageIds,
        syncStatus: 'pending',
        version: 1
      };

      // 5. 레포트 저장
      await this.saveToIndexedDB(this.stores.reports, sanitizedReport);

      // console.log('[OfflineStorage] 레포트 저장 완료:', {
      //   reportId: report.id,
      //   imageCount: imageIds.length
      // });

      // 🆕 저장 완료 이벤트 발생 (OfflineIndicator가 즉시 감지)
      this.notifyListeners('report_saved_offline', {
        reportId: report.id,
        reportName: report.name,
        imageCount: imageIds.length,
        timestamp: new Date()
      });

      // 🔥 중복 저장 방지: 생성된 ID가 포함된 레포트 객체 반환
      return sanitizedReport;
    } catch (error) {
      // console.error('[OfflineStorage] 레포트 저장 실패:', error);
      throw error;
    }
  }

  /**
   * 레포트에서 이미지 추출 및 별도 저장
   */
  async extractAndSaveImages(report) {
    const imageIds = [];

    if (!report.items) {
      // console.log('[OfflineStorage] 레포트에 items가 없음');
      return imageIds;
    }

    // console.log('[OfflineStorage] 이미지 추출 시작, sections:', report.items.length);

    for (const [sectionIdx, section] of report.items.entries()) {
      if (!section.items) continue;

      // console.log(`[OfflineStorage] Section ${sectionIdx} 처리 중, fields:`, section.items.length);

      for (const [fieldIdx, field] of section.items.entries()) {
        // console.log(`[OfflineStorage] Field ${sectionIdx}-${fieldIdx} 체크:`, {
        //   type: field.type,
        //   hasAnswer: !!field.answer,
        //   hasFile: !!(field.answer && field.answer.file)
        // });

        if (field.type === 'image' && field.answer && field.answer.file) {
          const fieldPath = `items[${sectionIdx}].items[${fieldIdx}]`;
          // console.log(`[OfflineStorage] 이미지 필드 발견: ${fieldPath}`, {
          //   fileName: field.answer.file.name,
          //   fileSize: field.answer.file.size,
          //   fileType: field.answer.file.type
          // });

          try {
            const imageId = await this.saveImage(
              field.answer.file,
              report.id,
              fieldPath,
              field.answer
            );
            imageIds.push({
              fieldPath,
              imageId,
              originalName: field.answer.name
            });
          } catch (error) {
            // console.error('[OfflineStorage] 이미지 저장 실패:', error);
            // 이미지 저장 실패해도 레포트는 저장 계속
          }
        }
      }
    }

    return imageIds;
  }

  /**
   * 이미지 파일 저장 (🆕 자동 압축 포함)
   */
  async saveImage(imageFile, reportId, fieldPath, metadata = {}) {
    try {
      const imageId = this.generateId();

      // 🆕 이미지 압축 (2MB 이상인 경우)
      // console.log(`[OfflineStorage] ⚡ 이미지 압축 시작: ${imageFile.name}`);
      // console.log(`[OfflineStorage] 📊 원본 크기: ${(imageFile.size / 1024 / 1024).toFixed(2)}MB`);

      const compressedFile = await autoCompressIfNeeded(imageFile);

      // console.log(`[OfflineStorage] ✅ 압축 완료: ${(compressedFile.size / 1024 / 1024).toFixed(2)}MB`);
      // console.log(`[OfflineStorage] 📉 압축률: ${compressedFile.size !== imageFile.size ? (((imageFile.size - compressedFile.size) / imageFile.size) * 100).toFixed(1) + '%' : '압축 안됨 (2MB 이하)'}`);

      // File 객체를 ArrayBuffer로 변환
      const arrayBuffer = await compressedFile.arrayBuffer();

      const imageData = {
        id: imageId,
        reportId: reportId,
        fieldPath: fieldPath,
        name: compressedFile.name,
        type: compressedFile.type,
        size: compressedFile.size,
        lastModified: compressedFile.lastModified,
        data: arrayBuffer,
        metadata: {
          ...metadata,
          upDate: metadata.upDate || new Date(compressedFile.lastModified).toLocaleString(),
          originalSize: imageFile.size, // 원본 크기 기록
          compressed: compressedFile.size !== imageFile.size // 압축 여부
        },
        savedAt: new Date().toISOString(),
        syncStatus: 'pending'
      };

      await this.saveToIndexedDB(this.stores.images, imageData);

      // console.log('[OfflineStorage] 이미지 저장 완료:', {
      //   imageId,
      //   reportId,
      //   fieldPath,
      //   originalSize: imageFile.size,
      //   compressedSize: compressedFile.size,
      //   reduction: imageFile.size !== compressedFile.size ? `${(((imageFile.size - compressedFile.size) / imageFile.size) * 100).toFixed(1)}%` : '0%'
      // });

      return imageId;
    } catch (error) {
      // console.error('[OfflineStorage] 이미지 저장 실패:', error);
      throw error;
    }
  }

  /**
   * 레포트 데이터에서 이미지 File 객체를 이미지 ID로 교체
   */
  replaceImagesWithIds(report, imageIds) {
    // 깊은 복사로 원본 변경 방지
    const sanitizedReport = JSON.parse(JSON.stringify(report));

    // 이미지 File 객체들 제거 및 ID로 교체
    imageIds.forEach(({ fieldPath, imageId, originalName }) => {
      const pathParts = fieldPath.split(/[[\].]+/).filter(Boolean);
      let current = sanitizedReport;

      // 필드까지 이동
      for (let i = 0; i < pathParts.length - 1; i++) {
        current = current[pathParts[i]];
      }

      // answer 객체에서 file 제거하고 imageId 추가
      if (current[pathParts[pathParts.length - 1]].answer) {
        const answer = current[pathParts[pathParts.length - 1]].answer;
        delete answer.file; // File 객체 제거
        answer.offlineImageId = imageId;
        answer.isOfflineStored = true;
      }
    });

    return sanitizedReport;
  }

  /**
   * 레포트 조회 (이미지 복원 포함)
   */
  async getReport(reportId) {
    try {
      const report = await this.getFromIndexedDB(this.stores.reports, reportId);
      if (!report) return null;

      // 이미지 데이터 복원
      if (report.offlineMetadata && report.offlineMetadata.hasImages) {
        await this.restoreReportImages(report);
      }

      return report;
    } catch (error) {
      // console.error('[OfflineStorage] 레포트 조회 실패:', error);
      throw error;
    }
  }

  /**
   * 모든 오프라인 레포트 조회
   */
  async getAllOfflineReports() {
    try {
      const reports = await this.getAllFromIndexedDB(this.stores.reports);

      // 각 레포트의 이미지 복원
      for (const report of reports) {
        if (report.offlineMetadata && report.offlineMetadata.hasImages) {
          await this.restoreReportImages(report);
        }
      }

      return reports;
    } catch (error) {
      // console.error('[OfflineStorage] 모든 레포트 조회 실패:', error);
      throw error;
    }
  }

  /**
   * 레포트의 이미지 데이터 복원
   */
  async restoreReportImages(report) {
    if (!report.offlineMetadata || !report.offlineMetadata.hasImages) {
      return;
    }

    const imageIds = report.offlineMetadata.imageIds;

    for (const { fieldPath, imageId } of imageIds) {
      try {
        const imageData = await this.getFromIndexedDB(this.stores.images, imageId);
        if (imageData) {
          // ArrayBuffer를 File 객체로 복원
          const file = new File([imageData.data], imageData.name, {
            type: imageData.type,
            lastModified: imageData.lastModified
          });

          // 필드 위치 찾기
          const pathParts = fieldPath.split(/[[\].]+/).filter(Boolean);
          let current = report;

          for (let i = 0; i < pathParts.length - 1; i++) {
            current = current[pathParts[i]];
          }

          // answer 객체에 복원된 File 객체 추가
          if (current[pathParts[pathParts.length - 1]].answer) {
            const answer = current[pathParts[pathParts.length - 1]].answer;
            answer.file = file;
            answer.isUploaded = false; // 오프라인에서 복원된 것이므로 업로드 필요
          }
        }
      } catch (error) {
        // console.error('[OfflineStorage] 이미지 복원 실패:', error);
        // 이미지 복원 실패해도 레포트는 반환
      }
    }
  }

  /**
   * 🆕 이미지 업로드 완료 상태 중간 저장
   * - 업로드 성공한 이미지 필드를 서버 파일명으로 갱신하고 복원 목록(imageIds)에서 제거
   * - 브라우저 재시작 후에도 업로드 완료된 이미지는 다시 업로드하지 않음
   */
  async markImageAsUploaded(reportId, fieldPath, serverFileName) {
    try {
      // 저장된 원본 레포트 조회 (이미지 복원 없이)
      const report = await this.getFromIndexedDB(this.stores.reports, reportId);
      if (!report) {
        return false;
      }

      // 1. 해당 필드의 answer를 업로드 완료 상태로 갱신
      const pathParts = fieldPath.split(/[[\].]+/).filter(Boolean);
      let current = report;
      for (let i = 0; i < pathParts.length - 1; i++) {
        current = current?.[pathParts[i]];
      }
      const field = current?.[pathParts[pathParts.length - 1]];
      if (field && field.answer) {
        field.answer.name = serverFileName;
        field.answer.isUploaded = true;
        delete field.answer.file;
        delete field.answer.offlineImageId;
        delete field.answer.isOfflineStored;
      }

      // 2. 복원 목록에서 업로드된 이미지 제거
      const imageIds = report.offlineMetadata?.imageIds || [];
      const uploadedEntry = imageIds.find(entry => entry.fieldPath === fieldPath);
      if (report.offlineMetadata) {
        report.offlineMetadata.imageIds = imageIds.filter(entry => entry.fieldPath !== fieldPath);
        report.offlineMetadata.hasImages = report.offlineMetadata.imageIds.length > 0;
      }

      // 3. 갱신된 레포트 저장
      await this.saveToIndexedDB(this.stores.reports, report);

      // 4. 업로드 완료된 이미지 데이터 삭제 (용량 확보 + 재복원 방지)
      if (uploadedEntry) {
        await this.deleteFromIndexedDB(this.stores.images, uploadedEntry.imageId);
      }

      // console.log('[OfflineStorage] 이미지 업로드 진행 상황 저장:', { reportId, fieldPath, serverFileName });
      return true;
    } catch (error) {
      // console.error('[OfflineStorage] 이미지 업로드 상태 저장 실패:', error);
      throw error;
    }
  }

  /**
   * 레포트 삭제 (관련 이미지 포함)
   */
  async deleteReport(reportId) {
    try {
      // 관련 이미지들 먼저 삭제
      const images = await this.getImagesByReportId(reportId);
      for (const image of images) {
        await this.deleteFromIndexedDB(this.stores.images, image.id);
      }

      // 레포트 삭제
      await this.deleteFromIndexedDB(this.stores.reports, reportId);

      // console.log('[OfflineStorage] 레포트 삭제 완료:', reportId);

      // 🆕 삭제 완료 이벤트 발생 (OfflineIndicator가 즉시 감지하여 카운트 업데이트)
      this.notifyListeners('report_deleted_offline', {
        reportId,
        timestamp: new Date()
      });
    } catch (error) {
      // console.error('[OfflineStorage] 레포트 삭제 실패:', error);
      throw error;
    }
  }

  /**
   * 특정 레포트의 모든 이미지 조회
   */
  async getImagesByReportId(reportId) {
    const db = await this.openIndexedDB();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction([this.stores.images], 'readonly');
      const store = transaction.objectStore('images');
      const index = store.index('reportId');
      const request = index.getAll(reportId);

      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  /**
   * 기본 IndexedDB 저장 (기존 디버깅 패널 로직)
   */
  async saveToIndexedDB(storeName, data) {
    const db = await this.openIndexedDB();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction([storeName], 'readwrite');
      const store = transaction.objectStore(storeName);
      const request = store.put(data);

      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  /**
   * 기본 IndexedDB 조회
   */
  async getFromIndexedDB(storeName, key) {
    const db = await this.openIndexedDB();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction([storeName], 'readonly');
      const store = transaction.objectStore(storeName);
      const request = store.get(key);

      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  /**
   * 기본 IndexedDB 전체 조회
   */
  async getAllFromIndexedDB(storeName) {
    const db = await this.openIndexedDB();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction([storeName], 'readonly');
      const store = transaction.objectStore(storeName);
      const request = store.getAll();

      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  /**
   * 기본 IndexedDB 삭제
   */
  async deleteFromIndexedDB(storeName, key) {
    const db = await this.openIndexedDB();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction([storeName], 'readwrite');
      const store = transaction.objectStore(storeName);
      const request = store.delete(key);

      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  /**
   * 스토리지 정보 조회
   */
  async getStorageInfo() {
    try {
      const reports = await this.getAllFromIndexedDB(this.stores.reports);
      const images = await this.getAllFromIndexedDB(this.stores.images);

      const totalImageSize = images.reduce((total, img) => total + (img.size || 0), 0);

      return {
        reportCount: reports.length,
        imageCount: images.length,
        totalImageSize: totalImageSize,
        totalImageSizeMB: (totalImageSize / 1024 / 1024).toFixed(2)
      };
    } catch (error) {
      // console.error('[OfflineStorage] 스토리지 정보 조회 실패:', error);
      return {
        reportCount: 0,
        imageCount: 0,
        totalImageSize: 0,
        totalImageSizeMB: '0.00'
      };
    }
  }

  /**
   * 전체 오프라인 데이터 삭제
   */
  async clearAllOfflineData() {
    try {
      const db = await this.openIndexedDB();

      // 모든 스토어 비우기
      for (const storeName of Object.values(this.stores)) {
        const transaction = db.transaction([storeName], 'readwrite');
        const store = transaction.objectStore(storeName);
        await new Promise((resolve, reject) => {
          const request = store.clear();
          request.onsuccess = () => resolve();
          request.onerror = () => reject(request.error);
        });
      }

      // console.log('[OfflineStorage] 모든 오프라인 데이터 삭제 완료');
    } catch (error) {
      // console.error('[OfflineStorage] 데이터 삭제 실패:', error);
      throw error;
    }
  }

  /**
   * 고유 ID 생성
   */
  generateId() {
    return 'offline_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
  }
}

// 싱글톤 인스턴스
const offlineStorage = new OfflineStorage();

// 편의 함수들
export const saveReportOffline = (report) => offlineStorage.saveReport(report);
export const getOfflineReport = (reportId) => offlineStorage.getReport(reportId);
export const getAllOfflineReports = () => offlineStorage.getAllOfflineReports();
export const deleteOfflineReport = (reportId) => offlineStorage.deleteReport(reportId);
export const markOfflineImageUploaded = (reportId, fieldPath, serverFileName) => offlineStorage.markImageAsUploaded(reportId, fieldPath, serverFileName);
export const getOfflineStorageInfo = () => offlineStorage.getStorageInfo();
export const clearAllOfflineData = () => offlineStorage.clearAllOfflineData();

// 🆕 리스너 관리 함수들
export const addOfflineStorageListener = (callback) => offlineStorage.addListener(callback);
export const removeOfflineStorageListener = (callback) => offlineStorage.removeListener(callback);

export default offlineStorage;